import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { saveRaw, useStore } from "../state/store";
import { decisions, settings, toast, ui, type Tab } from "../state/app";
import { matchesOf, onNewMatches, partner, partnerName, partnerSets } from "../state/partner";
import { entryFor } from "../data/load";
import { goTab, openOverlay, overlayName, route } from "../state/router";
import { SwipeView, FilterButton } from "./SwipeView";
import { ShortlistView } from "./Shortlist";
import { ExploreView } from "./Explore";
import { TogetherView } from "./Together";
import { FilterSheet } from "./FilterSheet";
import { SettingsSheet } from "./Settings";
import { NameOffSheet } from "./NameOff";
import { ChosenSheet } from "./Chosen";
import { NameDetailSheet } from "./NameDetail";
import { MatchModal, Toast } from "./MatchModal";
import { Onboarding } from "./Onboarding";
import { ConfirmDialog } from "./Confirm";
import { ErrorBoundary } from "./ErrorBoundary";
import { IconCards, IconCompass, IconGear, IconList, IconTwoHearts } from "./icons";

const SEEN_KEY = "nameling:matches-viewed";

function TabBar({ inert }: { inert?: boolean }) {
  const tab = useStore(route, (r) => r.tab);
  const map = useStore(decisions, (s) => s.map);
  const sets = useStore(partnerSets, (s) => s);
  const paired = useStore(partner, (s) => !!s.code);
  const shortlist = useMemo(() => Object.values(map).filter((d) => d.d > 0).length, [map]);
  // the Shortlist badge bounces when a name joins it
  const lastShortlist = useRef(shortlist);
  const [grew, setGrew] = useState(0);
  useEffect(() => {
    // a moment later, as the heart that flew there lands
    if (shortlist > lastShortlist.current && !settings.get().reduceMotion) window.setTimeout(() => setGrew((n) => n + 1), 520);
    lastShortlist.current = shortlist;
  }, [shortlist]);
  const matchCount = useMemo(() => matchesOf(map, sets).length, [map, sets]);
  let viewed = 0;
  try {
    viewed = Number(localStorage.getItem(SEEN_KEY) || 0);
  } catch {
    /* ignore */
  }
  useEffect(() => {
    if (tab === "together") {
      saveRaw(SEEN_KEY, String(matchCount));
    }
  }, [tab, matchCount]);
  const newMatches = paired && tab !== "together" ? Math.max(0, matchCount - viewed) : 0;

  const items: { id: Tab; label: string; icon: preact.JSX.Element; badge?: string }[] = [
    { id: "swipe", label: "Swipe", icon: <IconCards /> },
    { id: "list", label: "Shortlist", icon: <IconList />, badge: shortlist ? String(shortlist > 999 ? "999+" : shortlist) : undefined },
    { id: "explore", label: "Explore", icon: <IconCompass /> },
    { id: "together", label: "Together", icon: <IconTwoHearts />, badge: newMatches ? `${newMatches} new` : undefined },
  ];
  return (
    <nav class="tabbar" aria-label="Main" inert={inert}>
      {items.map((it) => (
        <button
          key={it.id}
          data-tab={it.id}
          class={`tab ${tab === it.id ? "on" : ""}`}
          aria-current={tab === it.id ? "page" : undefined}
          onClick={() => goTab(it.id)}
        >
          <span class="tab-icon">
            {it.icon}
            {it.badge && (
              <span
                key={it.id === "list" ? `grew${grew}` : it.id}
                class={`tab-badge ${it.id === "together" ? "hot" : ""} ${it.id === "list" && grew ? "pop" : ""}`}
              >
                {it.badge}
              </span>
            )}
          </span>
          <span class="tab-label">{it.label}</span>
        </button>
      ))}
    </nav>
  );
}

function Splash({ error }: { error?: string | null }) {
  return (
    <div class="splash">
      <div class="brand big">Nameling</div>
      {error ? (
        <>
          <p class="error-text">{error}</p>
          <button class="btn btn-primary" onClick={() => location.reload()}>
            Try again
          </button>
        </>
      ) : (
        <div class="loader" aria-label="Loading names" role="status">
          <span />
        </div>
      )}
    </div>
  );
}

export function App() {
  const core = useStore(ui, (s) => s.core);
  const err = useStore(ui, (s) => s.loadError);
  const onboarded = useStore(settings, (s) => s.onboarded);
  const r = useStore(route, (s) => s);

  useEffect(() => {
    document.getElementById("main")?.scrollTo(0, 0);
  }, [r.tab]);

  // a match your partner made says which name, and its celebration is a tap away
  useEffect(() => {
    if (!core) return;
    return onNewMatches((keys, away) => {
      const who = partnerName();
      const names = keys.map((k) => entryFor(core, k, decisions.get().map[k]?.n).name);
      const list =
        names.length <= 3
          ? names.join(names.length === 2 ? " and " : ", ").replace(/, ([^,]*)$/, " and $1")
          : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
      const text = away
        ? `While you were away, you and ${who} matched on ${list}`
        : keys.length === 1
          ? `New match: ${names[0]}. ${who} liked it too`
          : `New matches with ${who}: ${list}`;
      toast(
        text,
        keys.length === 1 ? { label: "See", run: () => ui.set({ match: keys[0] }) } : { label: "See all", run: () => goTab("together") },
      );
    });
  }, [core]);

  // the welcome steps don't wait for the names, and stay put (with what's been chosen) when the
  // names arrive part-way through them
  const welcome = !onboarded && (core || !err) && <Onboarding core={core} />;
  if (!core)
    return (
      <>
        <Splash error={err} />
        {welcome}
      </>
    );
  const nameKey = overlayName(r.overlay);

  return (
    <>
      <div class="app">
        {!__NATIVE__ && onboarded && (
          <a
            class="skip-link"
            href="#main"
            onClick={(ev) => {
              // the router would read #main as the Swipe tab
              ev.preventDefault();
              document.getElementById("main")?.focus();
            }}
          >
            Skip to content
          </a>
        )}
        {/* while the welcome screens are up, everything behind them is out of reach */}
        <header class="topbar" inert={!onboarded}>
          <button class="brand" onClick={() => goTab("swipe")} aria-label="Nameling, back to swiping">
            Nameling
          </button>
          <div class="topbar-actions">
            {r.tab === "swipe" && <FilterButton />}
            <button class="icon-btn" onClick={() => openOverlay("settings")} aria-label="Settings">
              <IconGear size={22} />
            </button>
          </div>
        </header>
        <main class={`view view-${r.tab}`} id="main" tabIndex={-1} inert={!onboarded}>
          <ErrorBoundary label={r.tab === "swipe" ? "the deck" : r.tab === "list" ? "your shortlist" : r.tab} key={r.tab}>
            {r.tab === "swipe" && <SwipeView core={core} />}
            {r.tab === "list" && <ShortlistView core={core} />}
            {r.tab === "explore" && <ExploreView core={core} />}
            {r.tab === "together" && <TogetherView core={core} />}
          </ErrorBoundary>
        </main>
        <TabBar inert={!onboarded} />
        {/* a name link opened on a first visit shows its name once the welcome screens are done */}
        {onboarded && (
          <>
            <FilterSheet core={core} open={r.overlay === "filters"} />
            <SettingsSheet core={core} open={r.overlay === "settings"} />
            <NameOffSheet core={core} open={r.overlay === "nameoff"} />
            <ChosenSheet core={core} open={r.overlay === "chosen"} />
            <ErrorBoundary label="this name">
              <NameDetailSheet core={core} k={nameKey} />
            </ErrorBoundary>
            <MatchModal core={core} />
            <ConfirmDialog />
          </>
        )}
        <Toast />
      </div>
      {welcome}
    </>
  );
}
